/**
 * Shared helpers for authenticated fetch requests.
 *
 * The helper tries to attach a persisted Keycloak bearer token to the
 * outgoing request when the caller did not provide an `Authorization`
 * header. The logic is intentionally defensive so that stale tokens kept in
 * localStorage/sessionStorage do not override valid cookie based sessions.
 */

type MaybeToken = {
  token: string;
  expiresAt?: number;
};

const TOKEN_KEY_PATTERN = /token|keycloak/i;
const BEARER_PREFIX = /^bearer\s+/i;
const SAFETY_WINDOW_MS = 60_000; // Treat tokens expiring within a minute as stale.

const POSSIBLE_TOKEN_KEYS = [
  'token',
  'accessToken',
  'access_token',
  'idToken',
  'id_token',
  'bearer',
  'bearerToken',
  'jwt',
];

const POSSIBLE_EXPIRY_KEYS = [
  'expiresAt',
  'expires_at',
  'expiry',
  'expireAt',
  'expires',
  'exp',
  'accessTokenExpiresAt',
  'tokenExpiresAt',
  'access_token_expires_at',
];

function base64UrlDecode(input: string): string | null {
  try {
    const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
    if (typeof atob === 'function') {
      const decoded = atob(normalized);
      const percentEncoded = Array.from(decoded, (c: string) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`).join('');
      return decodeURIComponent(percentEncoded);
    }

    // Node.js fallback (tests)
    const BufferCtor = (globalThis as unknown as { Buffer?: { from: (input: string, encoding: string) => { toString(enc: string): string } } }).Buffer;
    if (BufferCtor && typeof BufferCtor.from === 'function') {
      return BufferCtor.from(normalized, 'base64').toString('utf8');
    }
    return null;
  } catch {
    return null;
  }
}

function decodeJwtExpiry(token: string): number | undefined {
  const parts = token.split('.');
  if (parts.length < 2) return undefined;

  const payload = base64UrlDecode(parts[1]);
  if (!payload) return undefined;

  try {
    const data = JSON.parse(payload) as { exp?: number };
    if (typeof data.exp === 'number' && data.exp > 0) {
      // exp is seconds since epoch.
      return data.exp * 1000;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function parseExpiry(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 1e12) {
      return value; // already milliseconds
    }
    if (value > 1e9) {
      return value * 1000; // seconds since epoch
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return parseExpiry(numeric);
    }

    const timestamp = Date.parse(trimmed);
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }

  return undefined;
}

function stripBearerPrefix(token: string): string {
  return token.replace(BEARER_PREFIX, '').trim();
}

function extractTokenFromObject(value: unknown, depth = 0): MaybeToken | undefined {
  if (!value || typeof value !== 'object' || depth > 3) {
    return undefined;
  }

  const record = value as Record<string, unknown>;

  for (const key of POSSIBLE_TOKEN_KEYS) {
    const candidate = record[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      const token = stripBearerPrefix(candidate);
      const expiresAt = POSSIBLE_EXPIRY_KEYS.map(k => parseExpiry(record[k])).find(
        exp => typeof exp === 'number',
      );
      return { token, expiresAt };
    }
  }

  // Try nested objects or JWT-looking strings.
  for (const entry of Object.values(record)) {
    if (typeof entry === 'string' && entry.includes('.')) {
      const token = stripBearerPrefix(entry);
      const exp = decodeJwtExpiry(token);
      if (exp) {
        return { token, expiresAt: exp };
      }
    }

    if (entry && typeof entry === 'object') {
      const nested = extractTokenFromObject(entry, depth + 1);
      if (nested) {
        return nested;
      }
    }
  }

  return undefined;
}

function parseTokenValue(raw: string): MaybeToken | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      return extractTokenFromObject(parsed);
    } catch {
      // fall through to string handling
    }
  }

  const token = stripBearerPrefix(trimmed);
  const expiresAt = decodeJwtExpiry(token);
  if (!expiresAt) {
    return undefined;
  }

  return { token, expiresAt };
}

function readFromStorage(storage: Storage | undefined | null): MaybeToken | undefined {
  if (!storage) return undefined;

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !TOKEN_KEY_PATTERN.test(key)) continue;

      const value = storage.getItem(key);
      if (!value) continue;

      const token = parseTokenValue(value);
      if (token) {
        return token;
      }
    }
  } catch {
    // Accessing storage can throw in private mode or restricted browsers – ignore silently.
    return undefined;
  }

  return undefined;
}

function hasActiveSessionCookie(): boolean {
  if (typeof document === 'undefined') return false;
  const cookieSource = document.cookie;
  if (!cookieSource) return false;

  return cookieSource
    .split(';')
    .map(entry => entry.trim())
    .filter(Boolean)
    .some(cookie => {
      const [name, value] = cookie.split('=');
      if (!value) return false;
      return /session/i.test(name);
    });
}

function resolveStoredBearerToken(): MaybeToken | undefined {
  for (const name of ['sessionStorage', 'localStorage'] as const) {
    try {
      // Accessing the storage property itself can throw, before readFromStorage runs.
      const token = readFromStorage(globalThis[name]);
      if (token) return token;
    } catch {
      // Keep trying the other storage when browser privacy settings deny access.
    }
  }
  return undefined;
}

function isTokenExpired(token: MaybeToken, now = Date.now()): boolean {
  if (!token.expiresAt) return true;
  return token.expiresAt - SAFETY_WINDOW_MS <= now;
}

function shouldSkipInjection(finalCredentials: RequestCredentials | undefined): boolean {
  if (!finalCredentials) return false;
  if (finalCredentials !== 'include' && finalCredentials !== 'same-origin') {
    return false;
  }

  return hasActiveSessionCookie();
}

/**
 * Adds a Bearer token to the headers if a non-expired token exists in storage
 * and the caller did not provide an explicit `Authorization` header.
 */
export function maybeAttachAuthorization(
  headers: Headers,
  finalCredentials: RequestCredentials | undefined,
): void {
  if (headers.has('Authorization')) return;
  if (shouldSkipInjection(finalCredentials)) return;

  const candidate = resolveStoredBearerToken();
  if (!candidate || isTokenExpired(candidate)) return;

  headers.set('Authorization', `Bearer ${candidate.token}`);
}

export function getStoredBearerToken(): string | undefined {
  const candidate = resolveStoredBearerToken();
  if (!candidate || isTokenExpired(candidate)) return undefined;
  return candidate.token;
}

